import { useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, MapPin, GraduationCap, DollarSign, Brain } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

// Generate consistent color based on name
const getAvatarGradient = (name) => {
  const gradients = [
    "from-yellow-500 to-amber-500",
    "from-amber-500 to-orange-500",
    "from-orange-500 to-red-500",
    "from-emerald-500 to-teal-500",
    "from-pink-500 to-rose-500",
    "from-purple-500 to-violet-500",
    "from-blue-500 to-cyan-500",
    "from-lime-500 to-green-500",
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

// Get initials from name
const getInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const PersonaCard = ({ persona, index, onClick, isThinking }) => {
  const { t } = useLanguage();
  const gradientClass = getAvatarGradient(persona.name);
  const [imageError, setImageError] = useState(false);
  
  // Get translated labels with fallback for non-standard values
  const getEducationLabel = (level) => {
    if (!level) return '';
    // Try with underscores first (e.g., "high_school")
    const translationKey = `education.${level}`;
    const translated = t(translationKey);
    // If translation returns the key itself, it means no translation exists
    if (translated === translationKey) {
      // Return the raw value without the "education." prefix
      return level;
    }
    return translated;
  };
  
  const getIncomeLabel = (level) => {
    if (!level) return '';
    const translationKey = `income.${level}`;
    const translated = t(translationKey);
    if (translated === translationKey) {
      return level;
    }
    return translated;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ 
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: index * 0.05 
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid={`persona-card-${persona.id}`}
      className="persona-card cursor-pointer relative overflow-hidden rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-yellow-500/30 p-5 group"
    >
      {/* Thinking overlay */}
      {isThinking && (
        <motion.div
          className="absolute inset-0 bg-yellow-500/10 backdrop-blur-[1px] z-20 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Brain className="w-8 h-8 text-yellow-400" />
          </motion.div>
        </motion.div>
      )}
      
      {/* Success flash animation on appear */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-amber-500/20"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
      />
      
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10">
        {/* Avatar */}
        <motion.div 
          className={`rounded-full bg-gradient-to-br ${gradientClass} overflow-hidden mb-4 mx-auto flex items-center justify-center w-16 h-16 relative`}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
        >
          {persona.avatar_base64 && !imageError ? (
            <img
              src={`data:image/jpeg;base64,${persona.avatar_base64}`}
              alt={persona.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="font-heading font-bold text-white text-lg">
              {getInitials(persona.name)}
            </span>
          )}
        </motion.div>

        {/* Name & Age */}
        <div className="text-center mb-3">
          <h3 className="font-heading font-semibold text-white text-base">
            {persona.name}
          </h3>
          <p className="text-sm text-zinc-500">
            {persona.age} • {persona.gender}
          </p>
        </div>

        {/* Occupation & Location */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Briefcase className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
            <span className="truncate">{persona.occupation}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <MapPin className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
            <span className="truncate">{persona.location}</span>
          </div>
        </div>

        {/* Education & Income badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {persona.education_level && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-300">
              <GraduationCap className="w-3 h-3" />
              {getEducationLabel(persona.education_level)}
            </span>
          )}
          {persona.household_income && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-xs text-amber-300">
              <DollarSign className="w-3 h-3" />
              {getIncomeLabel(persona.household_income)}
            </span>
          )}
        </div>

        {/* Traits Preview - Show first 3 traits only, no "+N" indicator */}
        <div className="flex flex-wrap gap-1.5">
          {persona.personality_traits.slice(0, 3).map((trait) => (
            <span 
              key={trait} 
              className="px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-xs text-yellow-300"
            >
              {trait}
            </span>
          ))}
        </div>

        {/* Tech Savviness Badge - Removed from absolute positioning */}
      </div>
    </motion.div>
  );
};

export default PersonaCard;
